import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import Icon from '../Icon';
import './Dropdown.css';

/**
 * Generic dropdown menu rendered via portal to avoid table overflow clipping.
 * items = [{ id, label, icon, onClick, danger, disabled, divider }]
 * `trigger` overrides the default button.
 */
const Dropdown = ({ trigger, label = 'Actions', items = [], align = 'right', className }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Compute portal menu position from the trigger's bounding rect.
  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const style = {
      position: 'fixed',
      top: rect.bottom + 6,
      zIndex: 350,
    };
    if (align === 'right') {
      style.right = window.innerWidth - rect.right;
    } else {
      style.left = rect.left;
    }
    setMenuStyle(style);
  }, [align]);

  // Recompute on scroll/resize while open.
  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open, updateMenuPosition]);

  // Click-outside: close if click lands outside BOTH the trigger AND the portal menu.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const handleItem = (item) => {
    if (item.disabled) return;
    setOpen(false);
    item.onClick?.();
  };

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={cn('kvo-dropdown__menu', `kvo-dropdown__menu--${align}`)}
          role="menu"
          style={menuStyle}
        >
          {items.map((item, i) =>
            item.divider ? (
              <div key={`d-${i}`} className="kvo-dropdown__divider" />
            ) : (
              <button
                key={item.id || i}
                type="button"
                role="menuitem"
                className={cn('kvo-dropdown__item', {
                  'kvo-dropdown__item--danger': item.danger,
                  'kvo-dropdown__item--disabled': item.disabled,
                })}
                onClick={() => handleItem(item)}
                disabled={item.disabled}
              >
                {item.icon && <Icon name={item.icon} size={15} />}
                {item.label}
              </button>
            )
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div
      className={cn('kvo-dropdown', { 'kvo-dropdown--open': open }, className)}
      ref={triggerRef}
    >
      <span className="kvo-dropdown__trigger" onClick={() => setOpen((v) => !v)}>
        {trigger || (
          <button type="button" className="kvo-dropdown__default-trigger">
            {label}
            <Icon name="chevronDown" size={14} />
          </button>
        )}
      </span>
      {menu}
    </div>
  );
};

export default Dropdown;
